import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  MdSearch,
  MdFavorite,
  MdPictureAsPdf,
  MdDownload,
  MdAutoAwesome,
  MdDelete,
} from "react-icons/md";
import axios from "axios";
import TeacherSidebar from "../../../components/gcstaff/TeacherSidebar/TeacherSidebar";
import "../../../assets/css/gcstaff/myPDFs/SavedPDFs.css";
import TeacherMasterLayout from "../../../layouts/gcstaff/staffmasterlayout/TeacherMasterLayout";
import AppContext from "../../../context/AppContext";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ── Page size control ── */
const ITEMS_PER_PAGE = 10;

// ─── Toast helper ─────────────────────────────────────────────
let toastTimer = null;

// ─── Component ────────────────────────────────────────────────
const SavedPDFs = () => {
  const [pdfs, setPdfs]           = useState([]);
  const [loading, setLoading]     = useState(false); // initial API fetch loading
  const [viewLoading, setViewLoading] = useState(false); // page-switch / search loading (sirf visible cards k liye)
  const [searchQuery, setSearch]  = useState("");
  const [toast, setToast]         = useState(null);   // { msg, out }
  const [currentPage, setCurrentPage] = useState(1);  // pagination state

  const { baseUrl, baseUrlForFiles, authToken, loggedInUser } = useContext(AppContext);

  // Pehli render par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  // ── Toast display ──
  const showToast = (msg) => {
    if (toastTimer) clearTimeout(toastTimer);
    setToast({ msg, out: false });
    toastTimer = setTimeout(() => {
      setToast((t) => t ? { ...t, out: true } : null);
      setTimeout(() => setToast(null), 320);
    }, 2200);
  };

  /*
    Raw shape coming from GET /teacher/ebooks/getSavedEbooks/{teacher_id}:
    {
      id: <favourite record id>,   ← used for delete (removeSavedEbook/{id})
      lbteacher_id, lbebook_id, ...
      ebook: { id, title, author, dept, pdf_file, img }   ← eager-loaded relation
    }
  */
  const mapSavedItem = (fav) => {
    const book = fav.ebook || {};
    return {
      id: fav.id, // favourite record id — NOT the ebook id
      image: book.img ? `${baseUrlForFiles}/${book.img}` : null,
      title: book.title,
      author: book.author,
      category: book.dept || "General",
      savedDate: fav.created_at
        ? new Date(fav.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
      pdfUrl: book.pdf_file ? `${baseUrlForFiles}/ebooks/${book.pdf_file}` : null,
    };
  };

  // ── Fetch saved PDFs ──
  useEffect(() => {
    if (!loggedInUser?.id) return;

    setLoading(true);
    axios
      .get(`${baseUrl}/teacher/ebooks/getSavedEbooks/${loggedInUser.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success === true) {
          setPdfs((res.data.ebooks || []).map(mapSavedItem));
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, authToken, loggedInUser]);

  // ── Filtered list (search) ──
  const displayed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return pdfs;
    return pdfs.filter(
      (p) =>
        p.title?.toLowerCase().includes(q) ||
        p.author?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
    );
  }, [pdfs, searchQuery]);

  /* ── Pagination slice (sirf current page ka data grid mein jayega) ── */
  const paginatedPdfs = displayed.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Search change hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /* ── Sirf visible cards ki loading dikhayein:
     - Jab pagination se dusre page par jayen
     - Jab search query change ho
     Poori list ek sath "pop" nahi hogi, sirf jitne records is waqt
     screen par show hone hain unki hi loading chalegi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery]);

  // ── Handlers ──
  const handleRead = (book) => {
    if (!book.pdfUrl) {
      showToast("PDF not available.");
      return;
    }
    window.open(book.pdfUrl, "_blank");
  };

  const handleDownload = (book) => {
    if (!book.pdfUrl) {
      showToast("PDF not available.");
      return;
    }
    window.open(book.pdfUrl, "_blank");
  };

  const handleFlash = (book) => showToast(`Flashcard feature coming soon.`);

  const handleDelete = (id, title) => {
    axios
      .delete(`${baseUrl}/teacher/ebooks/removeSavedEbook/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          setPdfs((prev) => prev.filter((p) => p.id !== id));
          showToast(`"${title}" removed from saved PDFs.`);
        }
      })
      .catch((err) => {
        console.error(err);
        showToast("Could not remove. Try again.");
      });
  };

  // ─── Render ───────────────────────────────────────────────
  return (
    <TeacherMasterLayout>
    <div className="gcStaff-pdfs-page">
      {/* Page heading */}
      <h2 className="gcStaff-pdf-heading">My Saved PDFs</h2>
      <p className="gcStaff-pdf-subtitle">
        Your wishlist of saved e-books from the digital library.
      </p>

      {/* Search bar */}
      <div className="gcStaff-pdf-search-wrap">
        <MdSearch className="gcStaff-pdf-search-icon" />
        <input
          className="gcStaff-pdf-search-input"
          placeholder="Search saved PDFs..."
          value={searchQuery}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Saved PDFs loading, Please wait..." />}

      {/* Cards grid */}
      {!loading && (
        <>
          <div className="gcStaff-pdf-grid">
            {viewLoading ? (
              /* ── Page-switch / search loading — sirf is section ki, baaki page affect nahi hota ── */
              <Loader message="Loading PDFs..." />
            ) : displayed.length === 0 ? (
              <div className="gcStaff-pdf-empty">
                <div className="gcStaff-pdf-empty-icon">📄</div>
                <p>No saved PDFs found.</p>
              </div>
            ) : (
              paginatedPdfs.map((pdf) => (
                <div className="gcStaff-pdf-card" key={pdf.id}>
                  {/* Cover image */}
                  <div className="gcStaff-pdf-img-wrap">
                    {pdf.image ? (
                      <img
                        className="gcStaff-pdf-img"
                        src={pdf.image}
                        alt={pdf.title}
                        loading="lazy"
                      />
                    ) : (
                      <div className="gcStaff-pdf-img" style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "rgba(64,148,174,0.08)"
                      }}>
                        <MdPictureAsPdf style={{ fontSize: "2.4rem", opacity: 0.35 }} />
                      </div>
                    )}
                    {/* Favourite heart */}
                    <button
                      className="gcStaff-pdf-fav-btn"
                      aria-label="Remove from favourites"
                      onClick={() => handleDelete(pdf.id, pdf.title)}
                    >
                      <MdFavorite />
                    </button>
                  </div>

                  {/* Card body */}
                  <div className="gcStaff-pdf-card-body">
                    <span className="gcStaff-pdf-category">{pdf.category}</span>
                    <p className="gcStaff-pdf-book-title m-0">{pdf.title}</p>
                    <p className="gcStaff-pdf-book-author m-0">{pdf.author}</p>
                    <p className="gcStaff-pdf-saved-date">Saved: {pdf.savedDate}</p>

                    {/* Action buttons */}
                    <div className="gcStaff-pdf-actions">
                      {/* Read */}
                      <button
                        className="gcStaff-pdf-btn-read"
                        onClick={() => handleRead(pdf)}
                      >
                        <MdPictureAsPdf style={{ fontSize: "1rem" }} />
                        Read
                      </button>

                      {/* Download */}
                      <button
                        className="gcStaff-pdf-icon-btn"
                        title="Download"
                        onClick={() => handleDownload(pdf)}
                      >
                        <MdDownload />
                      </button>

                      {/* Create Flashcard */}
                      <button
                        className="gcStaff-pdf-icon-btn gcStaff-pdf-flash"
                        title="Create Flashcard"
                        onClick={() => handleFlash(pdf)}
                      >
                        <MdAutoAwesome />
                      </button>

                      {/* Delete */}
                      <button
                        className="gcStaff-pdf-icon-btn gcStaff-pdf-delete"
                        title="Delete"
                        onClick={() => handleDelete(pdf.id, pdf.title)}
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
          {!viewLoading && (
            <Pagination
              currentPage={currentPage}
              totalItems={displayed.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`gcStaff-pdf-toast${toast.out ? " gcStaff-toast-out" : ""}`}>
          {toast.msg}
        </div>
      )}
    </div>
    </TeacherMasterLayout>
  );
};

export default SavedPDFs;